import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(false)
  const [isPortfolioClickOpen, setIsPortfolioClickOpen] = useState(false)
  const location = useLocation()
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => location.pathname === path

  // Handle hover with delay
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setIsPortfolioOpen(true)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    // Add 800ms delay before hiding
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPortfolioOpen(false)
    }, 800)
  }

  // Handle click toggle
  const handlePortfolioClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsPortfolioClickOpen(!isPortfolioClickOpen)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsPortfolioClickOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const navigationItems = [
    { path: '/quote', label: 'Get a Quote' },
    { 
      path: '/portfolio', 
      label: 'Portfolio',
      hasDropdown: true,
      dropdownItems: [
        { path: '/portfolio', label: 'All Galleries' },
        { path: '/gallery/wedding-dresses-in-motion', label: 'Wedding Dresses in Motion' },
        { path: '/portfolio/engagements', label: 'Engagements' },
      ]
    },
    { path: '/wedding-venues', label: 'Wedding Venues' },
    { path: '/packages', label: 'Packages' },
    { path: '/about', label: 'About' },
    { path: '/raves', label: 'Raves' },
    { path: '/contact', label: 'Contact' },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-2xl font-light text-gray-900 tracking-wide">
                Duende Photo
              </h1>
              <p className="text-xs text-gray-500 uppercase tracking-widest">
                Fine Art Wedding Photography
              </p>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <div key={item.path} className="relative">
                  {item.hasDropdown ? (
                    <div 
                      ref={dropdownRef}
                      className="relative"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        onClick={handlePortfolioClick}
                        className={`flex items-center space-x-1 text-sm font-light tracking-wide transition-colors duration-200 ${
                          isActive(item.path) || location.pathname.startsWith('/gallery')
                            ? 'text-gray-900 border-b border-gray-900'
                            : 'text-gray-600 hover:text-gray-900'
                        } cursor-pointer`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          isPortfolioOpen || isPortfolioClickOpen ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {(isPortfolioOpen || isPortfolioClickOpen) && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-xl border border-gray-100 rounded-md z-50"
                             style={{
                               // Add padding area for easier hover
                               paddingTop: '4px',
                               marginTop: '-4px'
                             }}>
                          <div className="py-3 px-1">
                            {item.dropdownItems?.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.path}
                                to={dropdownItem.path}
                                onClick={() => {
                                  setIsPortfolioOpen(false)
                                  setIsPortfolioClickOpen(false)
                                }}
                                className="block px-4 py-3 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md mx-2 transition-all duration-200"
                              >
                                {dropdownItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-sm font-light tracking-wide transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'text-gray-900 border-b border-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-2 space-y-1">
              {navigationItems.map((item) => (
                <div key={item.path}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => {
                          if (item.path === '/portfolio') {
                            setIsPortfolioClickOpen(!isPortfolioClickOpen)
                          }
                        }}
                        className={`flex items-center justify-between w-full py-3 text-sm font-light tracking-wide transition-colors duration-200 ${
                          isActive(item.path)
                            ? 'text-gray-900'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                          isPortfolioClickOpen ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {isPortfolioClickOpen && (
                        <div className="pl-4 space-y-1 bg-gray-50 rounded-md py-2 mt-1">
                          {item.dropdownItems?.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.path}
                              to={dropdownItem.path}
                              onClick={() => {
                                setIsMenuOpen(false)
                                setIsPortfolioClickOpen(false)
                              }}
                              className="block py-2 px-3 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-all duration-200"
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block py-3 text-sm font-light tracking-wide transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-light text-gray-900 mb-4">Duende Photo</h3>
              <p className="text-sm text-gray-600 mb-4">
                Fine art wedding photography serving the San Francisco Bay Area since 2005.
              </p>
              <p className="text-xs text-gray-500">
                Winner of Diablo "Best East Bay 2021"
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
                Services
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>Wedding Photography</li>
                <li>Engagement Photography</li>
                <li>Fine Art Photography</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wide">
                Service Areas
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>San Francisco</li>
                <li>Napa Valley</li>
                <li>Sonoma County</li>
                <li>Bay Area</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              © 2025 Duende Photo. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
